/* The moment after a self-service guest pays: the full-screen "it worked"
   screen that replaces a one-line banner nobody reads standing at a counter.
   It answers the four things a guest wants in that second — it went through,
   how long, where to collect, what they were charged — and gives them the two
   ways onward (follow the order, or order more).

   Kept OUT of CustomerMenu.js on purpose: that page re-renders on every status
   poll, and a component declared inside it would remount (and restart every
   animation) each time.

   Every string here goes through t() — this is a guest-facing surface, so the
   EN/FR rule applies in full; only the guest's own dish names are printed as
   the restaurant wrote them. */
import React, { useEffect, useState } from 'react';
import {
  Clock, MapPin, Utensils, Flame, BellRing, Plus, Receipt,
  Frown, Meh, Smile,
} from 'lucide-react';
import { useT } from '../../context/LanguageContext';

const ORANGE = '#EA6A12';
const GREEN = '#4E8B27';

/* Hand-placed confetti rather than a random scatter: the bits are kept out of
   the badge's own space and off the title, so the celebration never sits on top
   of the words it is celebrating. dx/rot drive the drift in the keyframes. */
const CONFETTI = [
  { left: '10%', dx: '-18px', rot: '-140deg', size: 9, color: '#F5C242', round: true, delay: 0.05 },
  { left: '22%', dx: '12px', rot: '120deg', size: 7, color: '#E8734A', round: false, delay: 0.22 },
  { left: '31%', dx: '-10px', rot: '200deg', size: 6, color: '#7FB069', round: true, delay: 0.4 },
  { left: '44%', dx: '16px', rot: '-90deg', size: 8, color: '#6FA8DC', round: false, delay: 0.12 },
  { left: '57%', dx: '-14px', rot: '160deg', size: 7, color: '#F5C242', round: true, delay: 0.34 },
  { left: '68%', dx: '10px', rot: '-200deg', size: 9, color: '#E8734A', round: false, delay: 0.18 },
  { left: '79%', dx: '-16px', rot: '110deg', size: 6, color: '#7FB069', round: true, delay: 0.46 },
  { left: '90%', dx: '8px', rot: '-130deg', size: 8, color: '#6FA8DC', round: false, delay: 0.28 },
];

// Soft plate tints for the dish rows, rotated so a short order still looks laid out.
const ROW_TINTS = [
  { bg: '#FBEEE4', fg: '#C2612A' },
  { bg: '#E9F3E4', fg: '#4E8B27' },
  { bg: '#E7F0F7', fg: '#3A6E96' },
];

const FACES = [
  { score: 1, Icon: Frown, bg: '#FBDDE0', fg: '#C4485B', key: 'guest.menu.success.rate.bad' },
  { score: 2, Icon: Meh, bg: '#FBE7C6', fg: '#B77A16', key: 'guest.menu.success.rate.ok' },
  { score: 3, Icon: Smile, bg: '#DFF0D6', fg: '#4E8B27', key: 'guest.menu.success.rate.great' },
];

/**
 * @param {object}   props
 * @param {string}   props.orderNumber   the number the guest quotes at the counter
 * @param {Array}    props.items         [{name, variant, qty, unitPrice, image}]
 * @param {number}   props.total         what the order came to
 * @param {boolean}  props.paid          true = settled on the way in (self-service)
 * @param {number}   props.etaMinutes    kitchen estimate, from the dishes' own cook times
 * @param {string}   props.pickupLabel   "Pick up at" / "Served at" — already translated
 * @param {string}   props.pickupValue   the counter or table it lands at (restaurant's own words)
 * @param {Function} props.formatPrice   the page's currency formatter, so amounts match the bill
 * @param {Function} props.onTrack       → the Orders tab
 * @param {Function} props.onAddMore     → back to the menu
 */
export default function OrderPlacedCelebration({
  orderNumber, items = [], total = 0, paid = false, etaMinutes = 0,
  pickupLabel = '', pickupValue = '', formatPrice = (n) => n,
  onTrack, onAddMore,
}) {
  const { t } = useT();
  /* Mock, like the rest of the guest's payment flow: there is no rating
     endpoint yet, so the tap is kept for this tab and simply thanked. */
  const [rating, setRating] = useState(0);

  // Opening over a scrolled menu, so the celebration starts at its own top.
  useEffect(() => { window.scrollTo?.(0, 0); }, []);

  const rate = (score) => {
    setRating(score);
    try { sessionStorage.setItem('customerOrderRating', String(score)); } catch { /* no storage */ }
  };

  const steps = [
    { key: 'guest.menu.success.step.placed', Icon: null, state: 'done' },
    { key: 'guest.menu.success.step.cooking', Icon: Flame, state: 'next' },
    { key: 'guest.menu.success.step.ready', Icon: BellRing, state: 'later' },
  ];

  return (
    <div
      className="fixed inset-0 z-[55] overflow-y-auto opc-root"
      role="dialog"
      aria-modal="true"
      aria-label={t('guest.menu.success.title')}
    >
      <style>{`
        .opc-root { background: #FAF4EC; }
        @keyframes opc-pop {
          0%   { transform: scale(.2); opacity: 0; }
          55%  { transform: scale(1.14); opacity: 1; }
          78%  { transform: scale(.95); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes opc-halo {
          0%   { transform: scale(.65); opacity: .5; }
          100% { transform: scale(2); opacity: 0; }
        }
        @keyframes opc-draw { to { stroke-dashoffset: 0; } }
        @keyframes opc-rise {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes opc-confetti {
          0%   { opacity: 0; transform: translate3d(0, -24px, 0) rotate(0) scale(.5); }
          18%  { opacity: 1; }
          100% { opacity: 0; transform: translate3d(var(--dx), 128px, 0) rotate(var(--rot)) scale(1); }
        }
        @keyframes opc-flicker {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.16); }
        }
        @keyframes opc-ring {
          0%   { transform: scale(.8); opacity: .55; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        .opc-badge  { animation: opc-pop .62s cubic-bezier(.22,1.2,.36,1) both; }
        .opc-halo   { animation: opc-halo 1.5s ease-out .3s 2 both; }
        .opc-tick   { stroke-dasharray: 34; stroke-dashoffset: 34;
                      animation: opc-draw .34s ease-out .42s forwards; }
        .opc-rise   { animation: opc-rise .5s ease-out both; }
        .opc-bit    { animation: opc-confetti 1.7s ease-in both; }
        .opc-flame  { animation: opc-flicker 1.6s ease-in-out infinite; }
        .opc-ring   { animation: opc-ring 1.8s ease-out infinite; }
        /* A guest who asked their phone to calm down gets the same screen,
           standing still — nothing here carries meaning through motion. */
        @media (prefers-reduced-motion: reduce) {
          .opc-badge, .opc-halo, .opc-tick, .opc-rise, .opc-flame, .opc-ring {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
            stroke-dashoffset: 0 !important;
          }
          .opc-bit { display: none; }
        }
      `}</style>

      <div className="mx-auto w-full max-w-md px-4 pt-10 pb-8">
        {/* --- The tick, with its confetti ------------------------------- */}
        <div className="relative flex justify-center" aria-hidden="true">
          <div className="pointer-events-none absolute inset-x-0 -top-6 h-40 overflow-hidden">
            {CONFETTI.map((c, i) => (
              <span
                key={i}
                className="opc-bit absolute block"
                style={{
                  left: c.left,
                  top: 0,
                  width: c.size,
                  height: c.size,
                  background: c.color,
                  borderRadius: c.round ? '9999px' : '2px',
                  animationDelay: `${c.delay}s`,
                  '--dx': c.dx,
                  '--rot': c.rot,
                }}
              />
            ))}
          </div>
          <span className="relative flex h-24 w-24 items-center justify-center">
            <span
              className="opc-halo absolute inset-0 rounded-full"
              style={{ background: 'rgba(78,139,39,.28)' }}
            />
            <span
              className="opc-badge relative flex h-24 w-24 items-center justify-center rounded-full"
              style={{ background: GREEN, boxShadow: '0 12px 28px rgba(78,139,39,.32)' }}
            >
              <svg viewBox="0 0 32 32" className="h-12 w-12" fill="none">
                <path
                  className="opc-tick"
                  d="M8 16.5 L14 22.5 L24.5 10.5"
                  stroke="#fff"
                  strokeWidth="3.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
        </div>

        {/* --- What happened -------------------------------------------- */}
        <h2
          className="opc-rise mt-6 text-center text-[26px] font-semibold leading-tight text-gray-900"
          style={{ animationDelay: '.35s' }}
        >
          {t('guest.menu.success.title')}
        </h2>
        <p
          className="opc-rise mt-2 text-center text-[15px] leading-snug text-gray-500"
          style={{ animationDelay: '.42s' }}
        >
          {t('guest.menu.success.subtitle')}
        </p>
        {orderNumber && (
          <p
            className="opc-rise mt-2 text-center text-xs font-medium tracking-wide text-gray-400"
            style={{ animationDelay: '.46s' }}
          >
            {t('guest.menu.notice.orderLine', { number: orderNumber })}
          </p>
        )}

        {/* --- How long, and where -------------------------------------- */}
        <div
          className="opc-rise mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2"
          style={{ animationDelay: '.5s' }}
        >
          {etaMinutes > 0 && (
            <div className="rounded-2xl bg-white p-3.5 shadow-sm">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Clock className="h-3.5 w-3.5" style={{ color: GREEN }} />
                {t('guest.menu.success.readyIn')}
              </div>
              <div className="mt-1 text-[22px] font-semibold text-gray-900">
                {t('guest.menu.success.minutes', { count: etaMinutes })}
              </div>
            </div>
          )}
          {pickupValue && (
            <div className="rounded-2xl bg-white p-3.5 shadow-sm">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <MapPin className="h-3.5 w-3.5" style={{ color: ORANGE }} />
                {pickupLabel}
              </div>
              <div className="mt-1 truncate text-[22px] font-semibold text-gray-900">
                {pickupValue}
              </div>
            </div>
          )}
        </div>

        {/* --- What they ordered, and what it cost ----------------------- */}
        <div
          className="opc-rise mt-3 rounded-2xl bg-white p-4 shadow-sm"
          style={{ animationDelay: '.58s' }}
        >
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            {t('guest.menu.success.yourOrder')}
          </div>
          <ul className="mt-3 space-y-3">
            {items.map((l, i) => {
              const tint = ROW_TINTS[i % ROW_TINTS.length];
              return (
                <li key={i} className="flex items-center gap-3">
                  {l.image ? (
                    <img
                      src={l.image}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                      style={{ background: tint.bg, color: tint.fg }}
                    >
                      <Utensils className="h-4 w-4" />
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] text-gray-900">{l.name}</span>
                    {l.variant && (
                      <span className="block truncate text-xs text-gray-400">{l.variant}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-sm text-gray-400">&times;{l.qty}</span>
                  <span className="shrink-0 text-sm font-semibold text-gray-900">
                    {formatPrice(l.unitPrice * l.qty)}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
            <span className="text-[15px] text-gray-700">
              {paid ? t('guest.menu.success.paid') : t('guest.menu.orderTotal')}
            </span>
            <span className="text-lg font-bold" style={{ color: ORANGE }}>
              {formatPrice(total)}
            </span>
          </div>
        </div>

        {/* --- Where the food is up to ----------------------------------- */}
        <div
          className="opc-rise mt-5 flex items-start justify-center gap-1"
          style={{ animationDelay: '.66s' }}
        >
          {steps.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 && <span className="mt-5 h-px w-8 shrink-0 bg-gray-200 sm:w-12" />}
              <span className="flex w-20 flex-col items-center gap-1.5">
                <span className="relative flex h-11 w-11 items-center justify-center">
                  {s.state === 'next' && (
                    <span
                      className="opc-ring absolute inset-0 rounded-full"
                      style={{ background: 'rgba(232,150,38,.35)' }}
                    />
                  )}
                  <span
                    className="relative flex h-11 w-11 items-center justify-center rounded-full"
                    style={
                      s.state === 'done'
                        ? { background: GREEN, color: '#fff' }
                        : s.state === 'next'
                          ? { background: '#F6C14E', color: '#7A4A06' }
                          : { background: '#ECEAE6', color: '#9CA3AF' }
                    }
                  >
                    {s.Icon ? (
                      <s.Icon className={`h-5 w-5 ${s.state === 'next' ? 'opc-flame' : ''}`} />
                    ) : (
                      /* The same drawn tick as the badge, so the stepper's
                         first stop reads as the thing that just happened. */
                      <svg viewBox="0 0 32 32" className="h-5 w-5" fill="none">
                        <path
                          d="M8 16.5 L14 22.5 L24.5 10.5"
                          stroke="#fff"
                          strokeWidth="3.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                </span>
                <span
                  className={`text-center text-[11px] ${
                    s.state === 'later' ? 'text-gray-400' : 'font-medium text-gray-600'
                  }`}
                >
                  {t(s.key)}
                </span>
              </span>
            </React.Fragment>
          ))}
        </div>

        {/* --- One tap of feedback, while the moment is fresh ------------- */}
        <div className="opc-rise mt-6 text-center" style={{ animationDelay: '.74s' }}>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            {rating ? t('guest.menu.success.rate.thanks') : t('guest.menu.success.rate.prompt')}
          </div>
          <div className="mt-2.5 flex items-center justify-center gap-3">
            {FACES.map(({ score, Icon, bg, fg, key }) => (
              <button
                key={score}
                type="button"
                onClick={() => rate(score)}
                aria-label={t(key)}
                aria-pressed={rating === score}
                className={`flex h-11 w-11 items-center justify-center rounded-full transition ${
                  rating && rating !== score ? 'opacity-40' : ''
                }`}
                style={{
                  background: bg,
                  color: fg,
                  boxShadow: rating === score ? `0 0 0 2.5px ${fg}` : 'none',
                }}
              >
                <Icon className="h-6 w-6" />
              </button>
            ))}
          </div>
        </div>

        {/* --- The two ways onward --------------------------------------- */}
        <div className="opc-rise mt-6 space-y-2.5" style={{ animationDelay: '.82s' }}>
          <button
            type="button"
            onClick={onTrack}
            className="flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-[15px] font-semibold text-white"
            style={{ background: ORANGE, boxShadow: '0 8px 20px rgba(234,106,18,.28)' }}
          >
            <Receipt className="h-[18px] w-[18px]" />
            {t('guest.menu.success.track')}
          </button>
          <button
            type="button"
            onClick={onAddMore}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-3.5 text-[15px] font-medium text-gray-700"
          >
            <Plus className="h-[18px] w-[18px]" />
            {t('guest.menu.success.addMore')}
          </button>
        </div>
      </div>
    </div>
  );
}
